import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const events = [
  { 
    name: 'Weddings', 
    icon: FiIcons.FiHeart, 
    description: "Your love story deserves more than a digital file. We provide an interactive experience where guests leave with a personalized heirloom that captures the elegance of your union."
  },
  { 
    name: 'Birthday Parties', 
    icon: FiIcons.FiGift, 
    description: "Instant gratification for all ages. We turn high-energy party moments into permanent fridge fixtures that keep the celebration alive for years to come." 
  },
  { 
    name: 'Quinceañeras', 
    icon: FiIcons.FiStar, 
    description: "A royal treatment for a milestone event. We capture the glamour, the family bonds, and the tradition in real-time, providing guests with a high-end gift." 
  },
  { 
    name: 'Bar Mitzvahs', 
    icon: FiIcons.FiAward, 
    description: "Bridging tradition with modern technology. We give your community a high-quality, tangible keepsake of this sacred and joyous transition." 
  },
  { 
    name: 'Corporate Branding', 
    icon: FiIcons.FiBriefcase, 
    description: "Elevate your brand beyond the boardroom. Our magnets integrate your logo into a gift people actually keep, ensuring your mission stays front-of-mind daily." 
  },
  { 
    name: 'Holiday Galas', 
    icon: FiIcons.FiMusic, 
    description: "The perfect festive favor. We capture the seasonal joy and collective team spirit, delivering a warm reminder of a successful year." 
  },
  { 
    name: 'Bridal Showers', 
    icon: FiIcons.FiCoffee, 
    description: "An intimate way to document the love and laughter. We provide a charming, low-pressure photo experience that celebrates the bride-to-be." 
  },
  { 
    name: 'Charity Fundraisers', 
    icon: FiIcons.FiUsers, 
    description: "Turn donors into ambassadors. Branded magnets serve as a daily reminder of your cause and the impact of their contribution." 
  },
];

const EventExperience = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);

  return (
    <section id="experience" className="py-24 bg-parlor-cream">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-parlor-green mb-4">The Event Experience</h2>
          <div className="w-24 h-1 bg-parlor-gold mx-auto"></div>
          <p className="mt-6 text-parlor-green/70 max-w-2xl mx-auto text-lg">
            Click an experience below to discover how we make your specific celebration unforgettable.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {events.map((event, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              onClick={() => setSelectedEvent(event)}
              className="group cursor-pointer bg-white p-8 border border-parlor-green/10 hover:border-parlor-gold transition-all duration-300 flex flex-col items-center justify-center text-center shadow-sm hover:shadow-md"
            >
              <SafeIcon icon={event.icon} className="text-3xl text-parlor-gold mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="font-serif text-lg text-parlor-green">{event.name}</h3>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal Popup */}
      <AnimatePresence>
        {selectedEvent && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedEvent(null)}
              className="absolute inset-0 bg-parlor-green/90 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative bg-parlor-cream max-w-lg w-full p-8 md:p-12 shadow-2xl border border-parlor-gold/20"
            >
              <button 
                onClick={() => setSelectedEvent(null)}
                className="absolute top-4 right-4 text-parlor-green/40 hover:text-parlor-green transition-colors"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-parlor-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={selectedEvent.icon} className="text-3xl text-parlor-gold" />
                </div>
                <h3 className="font-serif text-3xl text-parlor-green mb-4">{selectedEvent.name}</h3>
                <div className="w-12 h-0.5 bg-parlor-gold mx-auto mb-6"></div>
                <p className="text-parlor-green/80 text-lg leading-relaxed mb-8">
                  {selectedEvent.description}
                </p>
                <button 
                  onClick={() => {
                    setSelectedEvent(null);
                    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-parlor-green text-parlor-cream uppercase tracking-widest text-xs font-bold px-8 py-3 hover:bg-parlor-gold transition-colors"
                >
                  Inquire Now
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default EventExperience;